import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Users, ArrowRight, Sparkles, Settings, Archive, AlertTriangle, RefreshCw, Loader2, Radio, ExternalLink, PlayCircle, Home, UserRound, GraduationCap, Baby } from "lucide-react";
import ofuesiLogo from "@/assets/ofuesi-logo.png";
import {
  PublicConferenceDate,
  PublicConferenceRow,
  PublicKitEventRow,
  formatConferenceDateRange,
  parseConferenceDateValue,
  resolvePublicConference,
  toConferenceSlug,
} from "@/lib/conferencePublic";
import {
  fetchRegions,
  fetchUpcomingConferences,
  fetchKitEvents,
  fetchCounts,
  refreshCounts,
  fetchHeader,
  fetchPastConferencesFirstPage,
  fetchPastConferencesPage,
  invalidateConferenceHubCache,
  type ConferenceHubRegion,
} from "@/lib/conferencePrefetch";
import { LIVE_STREAMS } from "@/components/conference/LiveStreamCallout";


type RegionRow = ConferenceHubRegion;


type ConferenceDate = PublicConferenceDate;
type ConferenceRow = PublicConferenceRow;
type KitEventRow = PublicKitEventRow;

interface RegionCount {
  region: string;
  families: number;
  individuals: number;
  adults: number;
  students: number;
  teens: number;
  kids: number;
}

const daysUntil = (start?: string | null): number | null => {
  if (!start) return null;
  const target = parseConferenceDateValue(start);
  if (!target) return null;
  target.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86_400_000);
};

const ConferenceHub = () => {
  const navigate = useNavigate();
  const [regions, setRegions] = useState<RegionRow[]>([]);
  const [conferences, setConferences] = useState<ConferenceRow[]>([]);
  const [pastConferences, setPastConferences] = useState<ConferenceRow[]>([]);
  const [kitEvents, setKitEvents] = useState<KitEventRow[]>([]);
  const [counts, setCounts] = useState<RegionCount[]>([]);
  const [header, setHeader] = useState({ eyebrow: "Latest Conference", title: "OFUESI Annual Regional Conferences 2026", subtitle: "Pick your region to view details and register." });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "OFUESI Regional Conference 2026 — Choose Your Region";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Pick your region to view OFUESI Regional Conference 2026 details and register your family.",
    );
  }, []);

  const PAST_PAGE_SIZE = 40;
  const [pastPage, setPastPage] = useState(0);
  const [pastHasMore, setPastHasMore] = useState(false);
  const [pastLoadingMore, setPastLoadingMore] = useState(false);
  const [slow, setSlow] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // Surface a friendly "slow" UI quickly so users have a Retry option.
    const slowTimer = window.setTimeout(() => {
      if (!cancelled) setSlow(true);
    }, 3500);

    // Hard fallback: never leave the user stuck on the loading screen.
    // After 10s, render the page with whatever data has arrived (possibly empty)
    // so secondary fetches can continue to populate the UI in-place.
    const hardFallback = window.setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 20000);

    setLoading(true);
    setSlow(false);
    setLoadError(null);

    // Regions block first paint — auto-retry transient network errors (e.g. "Failed to fetch")
    // with exponential backoff so users don't get stuck on a one-off blip.
    const loadRegionsWithRetry = async () => {
      const delays = [0, 600, 1500, 3000]; // 4 attempts total
      let lastErr: unknown = null;
      for (let i = 0; i < delays.length; i++) {
        if (cancelled) return;
        if (delays[i] > 0) {
          await new Promise((r) => setTimeout(r, delays[i]));
          if (cancelled) return;
          // Bust module cache so we actually re-issue the request.
          invalidateConferenceHubCache();
        }
        try {
          const data = await fetchRegions();
          if (cancelled) return;
          setRegions(data);
          setLoadError(null);
          return;
        } catch (err) {
          lastErr = err;
          console.warn(`[ConferenceHub] regions attempt ${i + 1} failed:`, err);
        }
      }
      if (!cancelled) {
        console.error("[ConferenceHub] regions failed after retries:", lastErr);
        setLoadError(lastErr instanceof Error ? lastErr.message : "Failed to load regions");
      }
    };

    loadRegionsWithRetry().finally(() => {
      if (!cancelled) {
        setLoading(false);
        setSlow(false);
        window.clearTimeout(slowTimer);
        window.clearTimeout(hardFallback);
      }
    });

    // Secondary data — cache-aware, never blocks paint.
    fetchUpcomingConferences().then((data) => !cancelled && setConferences(data)).catch(() => {});
    fetchKitEvents().then((data) => !cancelled && setKitEvents(data)).catch(() => {});
    refreshCounts().then((data) => !cancelled && setCounts(data as RegionCount[])).catch(() => {});
    fetchHeader().then((data) => {
      if (!cancelled && data) setHeader((h) => ({ ...h, ...data }));
    }).catch(() => {});
    fetchPastConferencesFirstPage(PAST_PAGE_SIZE).then((data) => {
      if (cancelled) return;
      setPastConferences(data);
      setPastPage(0);
      setPastHasMore(data.length === PAST_PAGE_SIZE);
    }).catch(() => {});

    return () => {
      cancelled = true;
      window.clearTimeout(slowTimer);
      window.clearTimeout(hardFallback);
    };
  }, [retryNonce]);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    const updateCounts = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await refreshCounts();
        if (!cancelled) setCounts(data as RegionCount[]);
      } catch (err) {
        console.warn("[ConferenceHub] live count refresh failed:", err);
      } finally {
        inFlight = false;
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") void updateCounts();
    };

    const interval = window.setInterval(updateCounts, 15000);
    window.addEventListener("focus", updateCounts);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", updateCounts);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [retryNonce]);


  const handleRetry = useCallback(() => {
    invalidateConferenceHubCache();
    setRetryNonce((n) => n + 1);
  }, []);

  const handleLoadMorePast = useCallback(async () => {
    setPastLoadingMore(true);
    try {
      const next = pastPage + 1;
      const data = await fetchPastConferencesPage(next, PAST_PAGE_SIZE);
      setPastConferences((prev) => [...prev, ...data]);
      setPastPage(next);
      setPastHasMore(data.length === PAST_PAGE_SIZE);
    } catch (err) {
      console.warn("[ConferenceHub] load-more past failed:", err);
    } finally {
      setPastLoadingMore(false);
    }
  }, [pastPage]);




  const cards = useMemo(() => {
    const built = regions.map((r) => {
      const count = counts.find((c) => c.region?.toLowerCase().trim() === r.name.toLowerCase().trim());
      const resolved = resolvePublicConference({ region: r, conferences, kitEvents });
      const isSouthwest = resolved.slug === "southwest";
      return {
        region: r,
        slug: resolved.slug,
        displayName: isSouthwest ? "Denver OFUESI Conference" : r.name,
        displayDescription: isSouthwest ? null : r.description,
        displayDate: isSouthwest ? "Cancelled" : formatConferenceDateRange(resolved.start, resolved.end),
        start: resolved.start,
        end: resolved.end,
        isPlaceholder: isSouthwest ? false : resolved.isPlaceholder,
        status: isSouthwest ? "cancelled" : resolved.status,
        timeLabel: resolved.timeLabel,
        venue: resolved.venue,
        flyer: resolved.flyer,
        theme: resolved.theme,
        topic: resolved.topic,
        speakers: resolved.speakers,
        contactPhone: resolved.contactPhone,
        coordinatorName: resolved.coordinatorName,
        coordinatorPhone: resolved.coordinatorPhone,
        days: isSouthwest ? daysUntil("2026-10-02") : daysUntil(resolved.start),
        families: count?.families ?? 0,
        attendees: count?.individuals ?? 0,
        adults: count?.adults ?? 0,
        students: count?.students ?? 0,
        teens: count?.teens ?? 0,
        kids: count?.kids ?? 0,
      };
    });

    // Southern first, then the rest alphabetically
    return built.sort((a, b) => {
      const aS = a.region.name.toLowerCase().startsWith("southern") ? 0 : 1;
      const bS = b.region.name.toLowerCase().startsWith("southern") ? 0 : 1;
      if (aS !== bS) return aS - bS;
      return a.region.name.localeCompare(b.region.name);
    });
  }, [regions, conferences, kitEvents, counts]);

  // A card is concluded once its last day has passed (fall back to start date).
  const isConcludedCard = (c: (typeof cards)[number]) => {
    if (c.status === "cancelled") return true;
    const raw = c.slug === "southwest" ? "2026-10-04" : c.end || c.start;
    const d = raw ? parseConferenceDateValue(raw) : null;
    if (!d) return false;
    d.setHours(23, 59, 59, 999);
    return d.getTime() < Date.now();
  };

  const liveCards = cards.filter(
    (c) => LIVE_STREAMS[c.slug] && !isConcludedCard(c) && c.status?.toLowerCase() !== "cancelled",
  );
  const upcomingCards = cards.filter(
    (c) => !isConcludedCard(c) && !LIVE_STREAMS[c.slug] && c.status?.toLowerCase() !== "cancelled",
  );
  const concludedCards = cards.filter(
    (c) => isConcludedCard(c) && c.status?.toLowerCase() !== "cancelled",
  );

  const getCardStatus = (c: (typeof cards)[number]) => {
    if (c.status?.toLowerCase() === "cancelled") return "Cancelled";
    if (isConcludedCard(c)) return "Completed";
    if (c.start && c.days !== null && c.days <= 0) return "In Progress";
    return "Upcoming";
  };


  const totals = useMemo(
    () =>
      cards.reduce(
        (acc, c) => ({
          families: acc.families + c.families,
          attendees: acc.attendees + c.attendees,
          adults: acc.adults + c.adults,
          students: acc.students + c.students,
          teens: acc.teens + c.teens,
          kids: acc.kids + c.kids,
        }),
        { families: 0, attendees: 0, adults: 0, students: 0, teens: 0, kids: 0 },
      ),
    [cards],
  );

  // Group past conferences. Anything not explicitly a half-yearly / spring / mid-year
  // event is treated as a previous Regional Conference. Undated rows default to 2022.
  const groupedPast = useMemo(() => {
    const isOther = (name?: string | null) =>
      /(half[- ]?year|mid[- ]?year|spring|summer\s+camp|winter)/i.test(name || "");
    const yearOf = (p: ConferenceRow) => {
      const first = p.conference_dates?.[0];
      const d =
        p.conference_date ||
        first?.start_date ||
        first?.date ||
        null;
      if (d) {
        const yr = new Date(d).getFullYear();
        if (!Number.isNaN(yr)) return yr;
      }
      // Fall back to a 4-digit year mentioned in the conference name (e.g. "... 2024").
      const m = (p.conference_name || "").match(/\b(20\d{2})\b/);
      return m ? Number(m[1]) : new Date().getFullYear();
    };
    const buckets: Record<"regional" | "other", Record<string, ConferenceRow[]>> = {
      regional: {},
      other: {},
    };
    pastConferences.forEach((p) => {
      const key = isOther(p.conference_name) ? "other" : "regional";
      const label = String(yearOf(p));
      (buckets[key][label] ||= []).push(p);
    });
    const sortYears = (obj: Record<string, ConferenceRow[]>) =>
      Object.entries(obj).sort(([a], [b]) => Number(b) - Number(a));
    return {
      regional: sortYears(buckets.regional),
      other: sortYears(buckets.other),
    };
  }, [pastConferences]);

  // Previous Regional Conferences = archived rows + regional cards whose dates have passed,
  // grouped into year tabs (2026, 2025, …).
  const pastRegionalByYear = useMemo(() => {
    type PastItem = {
      id: string;
      name: string;
      regionName: string | null;
      regionColor: string | null;
      dateLabel: string | null;
      flyer: string | null;
      year: number;
    };
    const buckets: Record<string, PastItem[]> = {};
    const flyerMatchesYear = (flyer: string | null, year: number) => {
      if (!flyer) return null;
      const yearsInUrl = flyer.match(/20\d{2}/g);
      return yearsInUrl && yearsInUrl.some((value) => Number(value) !== year) ? null : flyer;
    };
    const seen = new Set<string>();
    const push = (item: PastItem) => {
      const key = `${item.regionName}|${item.year}`;
      const yearBucket = (buckets[String(item.year)] ||= []);
      const existingIdx = yearBucket.findIndex((i) => `${i.regionName}|${i.year}` === key);
      if (existingIdx >= 0) {
        // Prefer the duplicate that has a usable flyer so archived rows don't shadow live cards.
        if (!yearBucket[existingIdx].flyer && item.flyer) {
          yearBucket[existingIdx] = item;
        }
        return;
      }
      seen.add(key);
      yearBucket.push(item);
    };

    groupedPast.regional.forEach(([year, items]) => {
      items.forEach((p) => {
        const region = regions.find((r) => r.id === p.region_id);
        push({
          id: p.id,
          name: p.conference_name,
          regionName: region
            ? /southwest/i.test(region.name)
              ? "Denver OFUESI Conference"
              : region.name
            : null,
          regionColor: region?.color || null,
          dateLabel: p.conference_date
            ? new Date(p.conference_date).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : null,
          flyer: flyerMatchesYear(p.flyer_url || null, Number(year)),
          year: Number(year),
        });
      });
    });

    concludedCards.forEach((c) => {
      const raw = c.slug === "southwest" ? "2026-10-04" : c.end || c.start;
      const d = raw ? parseConferenceDateValue(raw) : null;
      push({
        id: `card-${c.region.id}`,
        name: c.displayName,
        regionName: c.region.name,
        regionColor: c.region.color || null,
        dateLabel: c.displayDate || null,
        flyer: flyerMatchesYear(c.flyer || null, d ? d.getFullYear() : new Date().getFullYear()),
        year: d ? d.getFullYear() : new Date().getFullYear(),
      });
    });

    return Object.entries(buckets)
      .map(([year, items]) => [year, items] as [string, PastItem[]])
      .sort(([a], [b]) => Number(b) - Number(a));
  }, [groupedPast.regional, concludedCards, regions]);

  const [pastYear, setPastYear] = useState<string | null>(null);
  const activePastYear = pastYear && pastRegionalByYear.some(([y]) => y === pastYear)
    ? pastYear
    : pastRegionalByYear[0]?.[0] ?? null;
  const activePastItems = pastRegionalByYear.find(([y]) => y === activePastYear)?.[1] ?? [];


  // Render the shell immediately and show skeletons in-place rather than a
  // full-screen splash — perceived performance is far more consistent that way.




  return (
    <div className="min-h-screen faith-pattern">
      {/* Header */}
      <section className="bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan py-12 relative">
        <div className="absolute inset-0 faith-pattern opacity-20" />
        <div className="container mx-auto px-4 text-center relative z-10 max-w-4xl text-white space-y-3">
          <Badge className="bg-ministry-gold/90 text-ministry-blue border-0 mb-2">
            <Sparkles className="w-3 h-3 mr-1" /> Regional Conference 2026
          </Badge>
          <h1 className="text-4xl lg:text-5xl font-bold drop-shadow-lg">
            OFUESI Regional Conference 2026
          </h1>
          <p className="text-lg text-white/90">Choose your region below to view details and register.</p>
        </div>
      </section>

      {/* Conference status and attendance overview */}
      <section className="border-y border-ministry-gold/30 bg-ministry-gold/10">
        <div className="container mx-auto px-4 py-7 space-y-5">
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-ministry-blue">2026 Conference Status & Attendance</h2>
            <p className="text-sm text-muted-foreground">Current status and registration totals for every region.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {[
              { icon: Home, label: "Families", value: totals.families },
              { icon: Users, label: "Attendees", value: totals.attendees },
              { icon: UserRound, label: "Adults", value: totals.adults },
              { icon: GraduationCap, label: "Students", value: totals.students },
              { icon: Users, label: "Teens", value: totals.teens },
              { icon: Baby, label: "Children", value: totals.kids },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="rounded-lg border bg-card px-3 py-3 text-center shadow-sm">
                <Icon className="mx-auto mb-1.5 h-4 w-4 text-primary" />
                <div className="text-xl font-bold text-foreground">{value}</div>
                <div className="text-[11px] font-medium uppercase text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cards.map((c) => {
              const status = getCardStatus(c);
              const statusClass = status === "Cancelled"
                ? "bg-destructive/10 text-destructive border-destructive/30"
                : status === "Completed"
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : status === "In Progress"
                    ? "bg-amber-50 text-amber-800 border-amber-300"
                    : "bg-primary/10 text-primary border-primary/20";
              return (
                <Link
                  key={c.region.id}
                  to={`/conference/${c.slug}`}
                  className="rounded-lg border bg-card p-3 shadow-sm transition hover:border-primary/50 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{c.displayName}</h3>
                                {c.status && (
                                  <Badge className={`mt-1 border-0 ${c.status === "cancelled" ? "bg-red-500" : "bg-emerald-500"} text-white text-[10px]`}>
                                    {c.status.toUpperCase()}
                                  </Badge>
                                )}
                      <p className="mt-0.5 text-xs text-muted-foreground">{c.displayDate}</p>
                    </div>
                    <Badge variant="outline" className={`shrink-0 text-[10px] ${statusClass}`}>{status}</Badge>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span><strong className="text-foreground">{c.families}</strong> families</span>
                    <span><strong className="text-foreground">{c.attendees}</strong> attendees</span>
                  </div>
                  {status === "Cancelled" && (
                    <p className="mt-2 text-xs font-semibold text-destructive">This conference has been cancelled.</p>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Region grid */}
      <section className="container mx-auto px-4 py-12">
        {slow && loading && (
          <div className="max-w-3xl mx-auto mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
            <p className="text-sm text-amber-900 flex-1 min-w-[180px]">
              The conference data is taking longer than usual to load. You can keep waiting or retry now.
            </p>
            <Button onClick={handleRetry} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Retry
            </Button>
          </div>
        )}
        {cards.length === 0 ? (
          loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-label="Loading regions">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-xl border-2 overflow-hidden h-[320px] bg-card animate-pulse">
                  <div className="h-40 bg-muted" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 w-2/3 bg-muted rounded" />
                    <div className="h-3 w-1/2 bg-muted rounded" />
                    <div className="h-3 w-3/4 bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div className="max-w-2xl mx-auto text-center py-16 px-6 bg-card border-2 border-dashed rounded-xl">
            <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-bold mb-2">No regions configured yet</h2>
            {loadError ? (
              <p className="text-sm text-destructive mb-4">
                We couldn't load regions ({loadError}). Check the browser console for details.
              </p>
            ) : (
              <p className="text-muted-foreground mb-6">
                Regions power the conference hub. An administrator needs to add at least one region before
                visitors can browse and register.
              </p>
            )}
            <div className="text-left bg-secondary/40 rounded-md p-4 text-sm space-y-1 mb-6">
              <p className="font-semibold">To get started:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Sign in as a Global Admin.</li>
                <li>Open Admin Dashboard → <span className="font-medium">Regions</span> tab.</li>
                <li>Create or activate the six US regions (Eastern, Midwest, Northwest, Southeast, Southern, Southwest).</li>
                <li>Return to <code>/conference</code> — cards will appear automatically.</li>
              </ol>
            </div>
            <Button asChild className="ministry-gradient">
              <Link to="/admin">
                <Settings className="w-4 h-4 mr-2" /> Open Admin Dashboard
              </Link>
            </Button>
          </div>
          )
        ) : (

          <div className="space-y-12">
            {upcomingCards.length > 0 && (
              <div>
                <div className="mb-5 text-center">
                  <h2 className="text-xl lg:text-2xl font-bold text-ministry-blue">Upcoming Regional Conferences</h2>
                  <p className="text-sm text-muted-foreground">Register now for a conference near you.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {upcomingCards.map((c, i) => (
                    <motion.div
                      key={c.region.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: i * 0.05 }}
                      whileHover={{ y: -4 }}
                    >
                      <Card className="overflow-hidden border-2 hover:border-primary/60 hover:shadow-xl transition-all h-full flex flex-col">
                        <Link
                          to={c.isPlaceholder && c.slug !== "southwest" ? "#" : `/ofuesi-2026-regional-conference/${c.slug}/conference/registration`}
                          target={c.isPlaceholder && c.slug !== "southwest" ? undefined : "_blank"}
                          rel="noopener"
                          onClick={(e) => { if (c.isPlaceholder && c.slug !== "southwest") e.preventDefault(); }}
                          className={`block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg ${c.isPlaceholder && c.slug !== "southwest" ? "cursor-not-allowed" : ""}`}
                          aria-disabled={c.isPlaceholder && c.slug !== "southwest"}
                          aria-label={`Open ${c.displayName} conference details`}
                        >
                          <div
                            className="h-40 bg-gradient-to-br from-primary/20 to-ministry-cyan/30 relative"
                            style={
                              c.flyer
                                ? { backgroundImage: `url(${c.flyer})`, backgroundSize: "cover", backgroundPosition: "center" }
                                : undefined
                            }
                          >
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <div className="absolute top-3 left-3">
                              <span
                                className="inline-block w-2 h-5 rounded-sm align-middle mr-2"
                                style={{ backgroundColor: c.region.color || "#3B82F6" }}
                              />
                            </div>
                            {!c.isPlaceholder && c.days !== null && c.days >= 0 && (
                              <Badge className="absolute top-3 right-3 bg-ministry-gold text-ministry-blue border-0 text-xs">
                                {c.days === 0 ? "Today" : `${c.days} days to go`}
                              </Badge>
                            )}
                            <div className="absolute bottom-3 left-4 right-4 text-white">
                              <h3 className="text-xl font-bold drop-shadow">{c.displayName}</h3>
                                {c.status && (
                                  <Badge className={`mt-1 border-0 ${c.status === "cancelled" ? "bg-red-500" : "bg-emerald-500"} text-white text-[10px]`}>
                                    {c.status.toUpperCase()}
                                  </Badge>
                                )}
                            </div>
                          </div>
                          <CardContent className="p-4 space-y-2 flex-1 flex flex-col">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
                              <span>{c.displayDate}</span>
                              {c.isPlaceholder && (
                                <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">
                                  Dates tentative
                                </Badge>
                              )}
                            </div>
                            {c.timeLabel && (
                              <div className="text-xs text-muted-foreground -mt-1 ml-6">{c.timeLabel}</div>
                            )}
                            {c.venue && (
                              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                                <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                                <span>{c.venue}</span>
                              </div>
                            )}
                            {c.theme && (
                              <div className="rounded-md bg-ministry-blue/5 border border-ministry-blue/10 px-3 py-1.5">
                                <div className="text-[10px] font-semibold text-ministry-blue uppercase tracking-wide">Theme</div>
                                <div className="text-sm font-medium text-ministry-blue">{c.theme}</div>
                              </div>
                            )}
                            <div className="text-sm bg-primary/5 border border-primary/10 rounded-lg px-3 py-2 mt-auto shadow-sm">
                              <div className="flex justify-between items-center text-[11px] uppercase tracking-wider font-bold text-primary mb-1"><span>Registration Status</span><span className="text-emerald-600">Live</span></div><div className="flex gap-4">
                              <div><div className="text-lg font-extrabold text-foreground leading-none">{c.families}</div><div className="text-[10px] text-muted-foreground font-medium uppercase mt-0.5">Families</div></div><div><div className="text-lg font-extrabold text-foreground leading-none">{c.attendees}</div><div className="text-[10px] text-muted-foreground font-medium uppercase mt-0.5">Attendees</div></div></div>
                            </div>
                            <Button className="w-full ministry-gradient text-white shadow-md text-sm" disabled={c.isPlaceholder && c.slug !== "southwest"}>
                              {c.isPlaceholder && c.slug !== "southwest" ? "Details coming soon" : "View Details & Register"}
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </CardContent>
                        </Link>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {liveCards.length > 0 && (
              <div>
                <div className="mb-5 text-center">
                  <div className="inline-flex items-center gap-2 mb-1">
                    <span className="relative inline-flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-70 animate-ping" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-red-600">Happening Now</span>
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-ministry-blue">Live Regional Conferences</h2>
                  <p className="text-sm text-muted-foreground">Join the regions currently streaming their sessions.</p>
                </div>
                <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
                  {liveCards.map((c, i) => {
                    const stream = LIVE_STREAMS[c.slug];
                    return (
                      <motion.div
                        key={c.region.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: i * 0.05 }}
                        whileHover={{ y: -4 }}
                      >
                        <Card className="overflow-hidden border-2 hover:border-primary/60 hover:shadow-xl transition-all h-full flex flex-col">
                          {stream && (
                            <div className="bg-gradient-to-r from-red-600 to-rose-500 px-3 py-2 flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 text-white">
                                <Radio className="w-4 h-4" />
                                <span className="text-xs font-semibold">Live now</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/conference/${c.slug}/live`)}
                                  className="inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-2.5 py-1"
                                >
                                  <PlayCircle className="w-3 h-3" /> Watch
                                </button>
                                <a
                                  href={stream.youtube}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-2.5 py-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  YouTube <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            </div>
                          )}
                          <Link
                            to={c.isPlaceholder && c.slug !== "southwest" ? "#" : `/ofuesi-2026-regional-conference/${c.slug}/conference/registration`}
                            target={c.isPlaceholder && c.slug !== "southwest" ? undefined : "_blank"}
                            rel="noopener"
                            onClick={(e) => { if (c.isPlaceholder && c.slug !== "southwest") e.preventDefault(); }}
                            className={`block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg ${c.isPlaceholder && c.slug !== "southwest" ? "cursor-not-allowed" : ""}`}
                            aria-disabled={c.isPlaceholder && c.slug !== "southwest"}
                            aria-label={`Open ${c.displayName} conference details`}
                          >
                            <div
                              className="h-32 bg-gradient-to-br from-primary/20 to-ministry-cyan/30 relative"
                              style={
                                c.flyer
                                  ? { backgroundImage: `url(${c.flyer})`, backgroundSize: "cover", backgroundPosition: "center" }
                                  : undefined
                              }
                            >
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                              <div className="absolute top-3 left-3">
                                <span
                                  className="inline-block w-2 h-5 rounded-sm align-middle mr-2"
                                  style={{ backgroundColor: c.region.color || "#3B82F6" }}
                                />
                              </div>
                              {!c.isPlaceholder && c.days !== null && c.days >= 0 && (
                                <Badge className="absolute top-3 right-3 bg-ministry-gold text-ministry-blue border-0 text-xs">
                                  {c.days === 0 ? "Today" : `${c.days} days to go`}
                                </Badge>
                              )}
                              <div className="absolute bottom-3 left-4 right-4 text-white">
                                <h3 className="text-xl font-bold drop-shadow">{c.displayName}</h3>
                                {c.status && (
                                  <Badge className={`mt-1 border-0 ${c.status === "cancelled" ? "bg-red-500" : "bg-emerald-500"} text-white text-[10px]`}>
                                    {c.status.toUpperCase()}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <CardContent className="p-4 space-y-2 flex-1 flex flex-col">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
                                <span>{c.displayDate}</span>
                                {c.isPlaceholder && (
                                  <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">
                                    Dates tentative
                                  </Badge>
                                )}
                              </div>
                              {c.timeLabel && (
                                <div className="text-xs text-muted-foreground -mt-1 ml-6">{c.timeLabel}</div>
                              )}
                              {c.venue && (
                                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                                  <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                                  <span>{c.venue}</span>
                                </div>
                              )}
                              {c.theme && (
                                <div className="rounded-md bg-ministry-blue/5 border border-ministry-blue/10 px-3 py-1.5">
                                  <div className="text-[10px] font-semibold text-ministry-blue uppercase tracking-wide">Theme</div>
                                  <div className="text-sm font-medium text-ministry-blue">{c.theme}</div>
                                </div>
                              )}
                              <div className="text-sm bg-primary/5 border border-primary/10 rounded-lg px-3 py-2 mt-auto shadow-sm">
                                <div className="flex justify-between items-center text-[11px] uppercase tracking-wider font-bold text-primary mb-1"><span>Registration Status</span><span className="text-emerald-600">Live</span></div><div className="flex gap-4">
                                <div><div className="text-lg font-extrabold text-foreground leading-none">{c.families}</div><div className="text-[10px] text-muted-foreground font-medium uppercase mt-0.5">Families</div></div><div><div className="text-lg font-extrabold text-foreground leading-none">{c.attendees}</div><div className="text-[10px] text-muted-foreground font-medium uppercase mt-0.5">Attendees</div></div></div>
                              </div>
                              <Button className="w-full bg-gradient-to-r from-sky-600 to-amber-500 hover:from-sky-700 hover:to-amber-600 text-white shadow-md text-sm" disabled={c.isPlaceholder && c.slug !== "southwest"}>
                                {c.isPlaceholder && c.slug !== "southwest" ? "Details coming soon" : "View Details & Register"}
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </CardContent>
                          </Link>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Past Conferences — regional archive in year tabs, then other conferences */}
      {(pastRegionalByYear.length > 0 || groupedPast.other.length > 0) && (
        <section className="container mx-auto px-4 pb-16 space-y-12">
          {pastRegionalByYear.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Archive className="w-5 h-5 text-primary" />
                <h2 className="text-2xl font-bold">Previous Regional Conferences</h2>
              </div>
              <div className="flex flex-wrap gap-2 mb-5">
                {pastRegionalByYear.map(([year, items]) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => setPastYear(year)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${
                      year === activePastYear
                        ? "bg-ministry-blue text-white border-ministry-blue"
                        : "bg-background text-ministry-blue border-border hover:border-primary/60"
                    }`}
                    aria-pressed={year === activePastYear}
                  >
                    {year}
                    <span className="ml-2 text-xs opacity-80">{items.length}</span>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {activePastItems.map((p) => (
                  <Card key={p.id} className="overflow-hidden hover:shadow-lg transition-all">
                    {p.flyer ? (
                      <img
                        src={p.flyer}
                        alt={`${p.name} flyer`}
                        className="w-full h-44 object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-44 bg-gradient-to-br from-primary/20 to-ministry-cyan/30" />
                    )}
                    <CardContent className="p-3 space-y-1">
                      {p.regionName && (
                        <div className="text-xs font-semibold flex items-center gap-1">
                          <span className="w-1 h-4" style={{ backgroundColor: p.regionColor || "#3B82F6" }} />
                          {p.regionName}
                        </div>
                      )}
                      <p className="text-sm font-medium line-clamp-2">{p.name}</p>
                      {p.dateLabel && <p className="text-xs text-muted-foreground">{p.dateLabel}</p>}
                      <Badge variant="outline" className="text-[10px] border-muted-foreground/40 text-muted-foreground">
                        Completed
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {groupedPast.other.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Archive className="w-5 h-5 text-primary" />
                <h2 className="text-2xl font-bold">Other Conferences</h2>
              </div>
              <div className="space-y-8">
                {groupedPast.other.map(([year, items]) => (
                  <div key={year}>
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-ministry-blue">{year}</h3>
                      <span className="text-xs text-muted-foreground">
                        {items.length} conference{items.length === 1 ? "" : "s"}
                      </span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {items.map((p) => {
                        const region = regions.find((r) => r.id === p.region_id);
                        return (
                          <Card key={p.id} className="overflow-hidden hover:shadow-lg transition-all">
                            {p.flyer_url ? (
                              <img
                                src={p.flyer_url}
                                alt={`${p.conference_name} flyer`}
                                className="w-full h-44 object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-44 bg-gradient-to-br from-primary/20 to-ministry-cyan/30" />
                            )}
                            <CardContent className="p-3 space-y-1">
                              {region && (
                                <div className="text-xs font-semibold flex items-center gap-1">
                                  <span className="w-1 h-4" style={{ backgroundColor: region.color || "#3B82F6" }} />
                                  {/southwest/i.test(region.name) ? "Denver OFUESI Conference" : region.name}
                                </div>
                              )}
                              <p className="text-sm font-medium line-clamp-2">{p.conference_name}</p>
                              {p.conference_date && (
                                <p className="text-xs text-muted-foreground">
                                  {new Date(p.conference_date).toLocaleDateString(undefined, {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </p>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pastHasMore && (
            <div className="text-center">
              <Button
                variant="outline"
                onClick={handleLoadMorePast}
                disabled={pastLoadingMore}
              >
                {pastLoadingMore ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
                ) : (
                  <>Load more past conferences</>
                )}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>

  );
};

export default ConferenceHub;
